import { Action, AdaptiveCard, CarouselEvent, CarouselInteractionEvent, ExecuteAction, HostConfig, OpenUrlAction, SerializationContext, ShowCardAction, SubmitAction, ToggleVisibilityAction, ValidationEvent, ValidationPhase, Versions } from "adaptivecards";
import { Template } from "adaptivecards-templating";
import MarkdownIt from "markdown-it";
import { Measures } from "../../../telemetry/Measures";
import { dashboard } from "../../../telemetry/TraceLogging";
import { suspendActionDebounceTimeoutLength } from "../../components/WidgetContainer/WidgetFrame/WidgetFrameDynamicFeed";
import { getHostInstanceManagerApi } from "../../externalApi/InstanceManagerApi";
import { getSystemTheme, getVisualTheme } from "../../externalApi/ThemeApi";
import { getAllAdaptiveCardWidgetStyles } from "./AdaptiveCardWidget.styles";
import hostConfigDark from "./windows-widget-dark.json";
import hostConfigLight from "./windows-widget-light.json";
export async function getHTMLElementFromCard(card, instance, height, width) {
    const isTombstoned = await instance.widgetRegistration.isTombstoned;
    let cardDiv = document.createElement("div");
    AdaptiveCard.onProcessMarkdown = function (text, result) {
        let mdit = new MarkdownIt();
        result.outputHtml = mdit.render(text);
        result.didProcess = true;
    };
    card.onExecuteAction = (action) => {
        // Immediately disable the action button on click.
        action.isEnabled = false;
        action.updateEnabledState();
        // Re-enable the button after the timeout has passed.
        // If the widget provider responds before timeout, we'll re-render which will effectively re-enable the button too.
        setTimeout(() => {
            action.isEnabled = true;
            action.updateEnabledState();
        }, suspendActionDebounceTimeoutLength);
        onACAction(instance, action);
    };
    card.onCarouselEvent = (carouselEvent) => {
        onACAction(instance, carouselEvent);
    };
    card.onInlineCardExpanded = (showCardAction) => {
        onACAction(instance, showCardAction);
    };
    let isDarkMode = await isDarkTheme();
    let hostConfig = isDarkMode ? hostConfigDark : hostConfigLight;
    card.hostConfig = new HostConfig(hostConfig);
    const renderedCard = card.render();
    if (renderedCard === undefined) {
        return undefined;
    }
    let styleNode = document.createElement("style");
    styleNode.textContent = getAllAdaptiveCardWidgetStyles(await isDarkTheme(), instance.getWidgetProperty("size"), height, width);
    cardDiv.appendChild(styleNode);
    cardDiv.appendChild(renderedCard);
    cardDiv.querySelectorAll('[role="link"]').forEach((node) => {
        node.style.cssText += "cursor:pointer";
    });
    // This ensures the correct accessibility experience
    cardDiv.querySelectorAll(".ac-container.ac-adaptiveCard").forEach((node) => {
        node.tabIndex += -1;
    });
    // Listener for click actions
    cardDiv.addEventListener("click", (event) => {
        cardClickListener(event, instance, isTombstoned);
    });
    cardDiv.querySelectorAll(".ac-media-playButton").forEach((element) => {
        element.addEventListener("click", (event) => {
            cardClickListener(event, instance, isTombstoned);
        });
    });
    return cardDiv;
}
function cardClickListener(event, instance, isTombstoned) {
    const element = event.target;
    let action = undefined;
    if (element.classList.contains("ac-anchor")) {
        action = "UrlClick";
        notifyWidgetProvider(instance, {
            "eventKind": "interaction",
            "interactionKind": "hyperlink",
            "url": element.href,
        });
        if (isTombstoned) {
            Measures.logEvent(dashboard, "TombstoneCardClick", 16777216 /* productAndServicePerformance */, {
                appId: instance.widgetRegistration.appId,
                providerId: instance.widgetRegistration.providerId,
                source: (instance.widgetDefinitionId.startsWith("com") ? 0 /* MSStartBackingApp */ : 1 /* PWAProvider */),
                widgetDefinitionId: instance.widgetDefinitionId,
                widgetInstanceId: instance.widgetId,
            });
        }
    }
    else if (element.classList.contains("ac-input")) {
        action = "InputElementClick";
    }
    sentWidgetInteractionTelemetry(instance, action);
    sendWebXTCardActionTelemetry(instance, undefined, action === "UrlClick");
}
export async function getVisualThemeString() {
    let visualTheme = await getVisualTheme();
    switch (visualTheme) {
        case 0:
            return "dark";
        case 1:
            return "light";
        case 2:
            return "highcontrastblack";
        case 3:
            return "highcontrastwhite";
    }
    return "light";
}
export async function isDarkTheme() {
    let systemColors = await getSystemTheme();
    let isDark = false;
    if (systemColors) {
        const systemBackgroundColor = systemColors.substring(0, 6);
        isDark = systemBackgroundColor === "000000";
    }
    return isDark;
}
export function sentWidgetInteractionTelemetry(instance, action) {
    const widgetModelApiFactory = window.__com_microsoft_dsh_widgetModelApiFactory;
    if (widgetModelApiFactory) {
        const signalApi = widgetModelApiFactory.tryGetApi("signals", 1, 1);
        if (signalApi) {
            let payload = {
                clickAction: {
                    actionType: action,
                    clickType: 1 /* content */,
                },
                widgetItem: {
                    appId: instance.widgetRegistration.appId,
                    isPeregrine: false,
                    isPlatform: instance.isPlatform,
                    providerId: instance.widgetRegistration.providerId,
                    widgetDefinitionId: instance.widgetDefinitionId,
                    widgetInstanceId: instance.widgetId,
                },
            };
            let signal = { name: "OnWidgetClick", payload: JSON.stringify(payload), signalType: "WDX" };
            signalApi.sendSignal(signal);
        }
    }
}
export function sendWebXTCardActionTelemetry(instance, actionId, isNav) {
    const widgetModelApiFactory = window.__com_microsoft_dsh_widgetModelApiFactory;
    if (widgetModelApiFactory) {
        const telemetryApi = widgetModelApiFactory.tryGetApi("telemetry", 1, 1);
        if (telemetryApi) {
            telemetryApi.sendWebXTWidgetCardActionTelemetryAsync(instance, actionId, isNav);
        }
    }
}
export async function onACAction(instance, action) {
    // Send telemetry for widget interaction
    let actionType = action instanceof Action ? action.getJsonTypeName() : CarouselInteractionEvent[action.type];
    sentWidgetInteractionTelemetry(instance, actionType);
    if (action instanceof ExecuteAction || action instanceof SubmitAction) {
        await handleExecuteOrSubmitAction(instance, action);
    }
    else if (action instanceof CarouselEvent) {
        // Platform requires carouselEventName to be in camelCase with the first letter being lowercase.
        let carouselEventName = CarouselInteractionEvent[action.type];
        carouselEventName = carouselEventName.replace(carouselEventName.charAt(0), carouselEventName.charAt(0).toLowerCase());
        await notifyWidgetProvider(instance, {
            activePageId: action.activeCarouselPageId !== undefined ? action.activeCarouselPageId : "",
            activePageIndex: action.activeCarouselPageIndex !== undefined ? action.activeCarouselPageIndex : null,
            carouselEventKind: action.type !== undefined ? action.type : null,
            carouselEventName: carouselEventName,
            carouselId: action.carouselId !== undefined ? action.carouselId : "",
            eventKind: "interaction",
            interactionKind: "carousel",
        });
        // Send WebXT non-nav telemetry
        sendWebXTCardActionTelemetry(instance, CarouselInteractionEvent[action.type], false);
    }
    else {
        let actionInfo = {};
        actionInfo.id = action.id ? action.id : "";
        actionInfo.title = action.title ? action.title : "";
        let platformActionInfo = {};
        platformActionInfo.actionId = action.id ? action.id : "";
        platformActionInfo.actionTitle = action.title ? action.title : "";
        platformActionInfo.eventKind = "interaction";
        // Special behavior for OpenUrl action
        if (action instanceof OpenUrlAction && action.url) {
            actionInfo.url = action.url;
            // This navigation will be processed by the host
            window.location.href = action.url;
            // Send WebXT nav telemetry
            sendWebXTCardActionTelemetry(instance, action.getJsonTypeName(), true);
            platformActionInfo.interactionKind = "action.openUrl";
            platformActionInfo.url = action.url;
            await notifyWidgetProvider(instance, platformActionInfo);
            if (instance.widgetRegistration.isTombstoned) {
                Measures.logEvent(dashboard, "TombstoneCardClick", 16777216 /* productAndServicePerformance */, {
                    appId: instance.widgetRegistration.appId,
                    providerId: instance.widgetRegistration.providerId,
                    source: (instance.widgetDefinitionId.startsWith("com") ? 0 /* MSStartBackingApp */ : 1 /* PWAProvider */),
                    widgetDefinitionId: instance.widgetDefinitionId,
                    widgetInstanceId: instance.widgetId,
                });
            }
        }
        else if (action instanceof ToggleVisibilityAction) {
            platformActionInfo.interactionKind = "action.toggleVisibility";
            let targetElementIds = Object.keys(action.targetElements);
            platformActionInfo.elementIds = targetElementIds.map((key) => {
                return key;
            });
            await notifyWidgetProvider(instance, platformActionInfo);
        }
        else if (action instanceof ShowCardAction) {
            platformActionInfo.interactionKind = "action.showCard";
            await notifyWidgetProvider(instance, platformActionInfo);
        }
    }
    // Dispatch click event for listeners to act on it. e.g. Action menu
    document.dispatchEvent(new Event("click"));
}
async function handleExecuteOrSubmitAction(instance, action) {
    let verb = "";
    if (action instanceof ExecuteAction && action.verb) {
        verb = action.verb;
    }
    let isTombstoned = instance.widgetRegistration.isTombstoned;
    let actionProps = new Array();
    if (!isTombstoned) {
        let hostInstanceManagerApi = await getHostInstanceManagerApi();
        if (!hostInstanceManagerApi) {
            Measures.logEvent(dashboard, "AdaptiveCardOnExecuteOrSubmitActionFailed", 16777216 /* productAndServicePerformance */, {
                appId: instance.widgetRegistration.appId,
                hresult: 0x80004001,
                providerId: instance.widgetRegistration.providerId,
                reason: "MissingProviderAPI",
                widgetDefinitionId: instance.widgetDefinitionId,
                widgetInstanceId: instance.widgetId,
            });
            return;
        }
        await hostInstanceManagerApi.handleAction(instance.widgetId, verb, JSON.stringify(action.data));
    }
    action instanceof ExecuteAction ? instance.notifyExecuteAction(actionProps) : instance.notifySubmitAction(actionProps);
    // Send WebXT non-nav telemetry
    sendWebXTCardActionTelemetry(instance, action.getJsonTypeName(), false);
}
export async function notifyWidgetProvider(widgetInstance, data) {
    if (widgetInstance.widgetRegistration.isTombstoned) {
        return;
    }
    else {
        let hostInstanceManagerApi = await getHostInstanceManagerApi();
        if (!hostInstanceManagerApi) {
            Measures.logEvent(dashboard, "NotifyAppFailed", 16777216 /* productAndServicePerformance */, {
                appId: widgetInstance.widgetRegistration.appId,
                hresult: 0x80004001,
                providerId: widgetInstance.widgetRegistration.providerId,
                reason: "MissingProviderAPI",
                widgetDefinitionId: widgetInstance.widgetDefinitionId,
                widgetInstanceId: widgetInstance.widgetId,
            });
            return;
        }
        // All payloads must contain "host" and "version" properties.
        // "host" refers to us, the Widgets board.
        // "version" is the payload schema version.
        // See https://github.com/microsoft/WidgetDeveloperPlatform/blob/develop/specs/APISpecs/OnAnalyticsInfoReported_OnErrorInfoReported.md.
        if (data) {
            data.host = "dashboard.microsoft.com";
            data.version = "1.0";
        }
        let providerData = JSON.stringify(data);
        hostInstanceManagerApi.handleNotify(widgetInstance.widgetId, providerData);
    }
    // Send Provider data about a widget's user interactions/render failures.
    Measures.logEvent(dashboard, "NotifiedACWidgetApp", 16777216 /* productAndServicePerformance */, {
        appId: widgetInstance.widgetRegistration.appId,
        indexedDBInstanceId: widgetInstance.instanceId,
        providerId: widgetInstance.widgetRegistration.providerId,
        widgetDefinitionId: widgetInstance.widgetDefinitionId,
        widgetInstanceId: widgetInstance.widgetId,
    });
}
export function createAdaptiveCard(instance, widgetAdaptiveCard) {
    Measures.logEvent(dashboard, "AdaptiveCardRenderStarted", 16777216 /* productAndServicePerformance */, {
        appId: instance.widgetRegistration.appId,
        isTombstoned: instance.widgetRegistration.isTombstoned,
        providerId: instance.widgetRegistration.providerId,
        widgetDefinitionId: instance.widgetRegistration.id,
        widgetInstanceId: instance.widgetId,
    });
    // Create card payload
    let card = new AdaptiveCard();
    let serializationContext = new SerializationContext();
    let cardTemplate, cardData, cardPayload;
    try {
        let template = JSON.parse(widgetAdaptiveCard.template);
        cardTemplate = new Template(template);
    }
    catch (e) {
        logParseFailure(instance, e, "template");
        return [undefined, "parsePayloadFailure"];
    }
    try {
        cardData = JSON.parse(widgetAdaptiveCard.data);
    }
    catch (e) {
        logParseFailure(instance, e, "data");
        return [undefined, "parsePayloadFailure"];
    }
    let hostContext = widgetAdaptiveCard.hostContext;
    const settingsApi = instance.tryGetApi("settings", 1, 1);
    if (cardData.isSettingsPayload === "false" && settingsApi) {
        settingsApi.hideSettings();
    }
    try {
        cardPayload = cardTemplate.expand({
            $host: hostContext,
            $root: cardData,
        });
    }
    catch (e) {
        logParseFailure(instance, e, "expandCardTemplate");
        return [undefined, "parsePayloadFailure"];
    }
    if (cardPayload === undefined) {
        notifyWidgetProvider(instance, { errorKind: "cardCreationFailed", message: "Failed to parse app payload" });
        return [undefined, "cardPayloadUndefined"];
    }
    // Parse payload into Adaptive Card and validate
    try {
        card.parse(cardPayload, serializationContext);
    }
    catch (e) {
        logParseFailure(instance, e, "cardPayload");
        return [undefined, "parsePayloadFailure"];
    }
    let allValidationEvents = [];
    for (let i = 0; i < serializationContext.eventCount; i++) {
        allValidationEvents.push(serializationContext.getEventAt(i));
    }
    allValidationEvents.push(...card.validateProperties().validationEvents);
    if (allValidationEvents.length > 0) {
        let [shouldRenderCard, isVersionMismatch] = processACValidationEvents(instance, allValidationEvents, card.version);
        if (!shouldRenderCard) {
            if (isVersionMismatch) {
                return [undefined, "checkAppVersionError"];
            }
            return [undefined, "criticalValidationFailure"];
        }
    }
    return [card, "none"];
}
function processACValidationEvents(widgetInstance, validationEvents, cardVersion) {
    let shouldRenderCard = true;
    let isVersionMismatch = cardVersion > Versions.latest;
    let errorList = [];
    // We don't want to render the card if there is a critical error
    for (let event of validationEvents) {
        let isCriticalError = false;
        switch (event.event) {
            case ValidationEvent.ActionTypeNotAllowed:
            case ValidationEvent.Deprecated:
            case ValidationEvent.ElementTypeNotAllowed:
            case ValidationEvent.InteractivityNotAllowed:
            case ValidationEvent.UnknownActionType:
            case ValidationEvent.UnknownElementType:
            case ValidationEvent.UnsupportedCardVersion:
            case ValidationEvent.UnsupportedProperty:
                isCriticalError = true;
                shouldRenderCard = false;
                break;
            default:
                break;
        }
        let source = event.source !== undefined ? event.source.toJSON() : undefined;
        errorList.push({
            isCriticalError: isCriticalError,
            message: event.message,
            source: JSON.stringify(source),
            sourceType: source ? source.type : "",
            validationEvent: event.event !== undefined ? ValidationEvent[event.event] : "",
            validationEventId: event.event !== undefined ? event.event.toString() : "",
            validationPhase: event.phase !== undefined ? ValidationPhase[event.phase] : "",
            validationPhaseId: event.phase !== undefined ? event.phase.toString() : "",
        });
    }
    // Log telemetry for errors
    // Don't log source and message as it could contain PII
    Measures.logEvent(dashboard, "CardValidationError", 16777216 /* productAndServicePerformance */, {
        appId: widgetInstance.widgetRegistration.appId,
        cardVersion: cardVersion !== undefined ? cardVersion.toString() : "",
        errorList: errorList.map((error) => ({
            isCriticalError: error.isCriticalError,
            sourceType: error.sourceType,
            validationEvent: error.validationEvent,
            validationEventId: error.validationEventId,
            validationPhase: error.validationPhase,
            validationPhaseId: error.validationPhaseId,
        })),
        providerId: widgetInstance.widgetRegistration.providerId,
        shouldRender: shouldRenderCard,
        supportedMaxVersion: Versions.latest.toString(),
        widgetDefinitionId: widgetInstance.widgetDefinitionId,
        widgetInstanceId: widgetInstance.widgetId,
    });
    notifyWidgetProvider(widgetInstance, {
        didRenderCard: shouldRenderCard,
        errorKind: "cardValidation",
        source: errorList.map((error) => {
            return error.source;
        }),
        validationEvents: errorList.map((error) => {
            return error.message;
        }),
    });
    return [shouldRenderCard, isVersionMismatch];
}
export function logParseFailure(instance, e, reason) {
    Measures.logEvent(dashboard, "AdaptiveCardCreationFailed", 16777216 /* productAndServicePerformance */, {
        appId: instance.widgetRegistration.appId,
        error: e.Message ? e.Message : e,
        isTombstoned: instance.widgetRegistration.isTombstoned,
        providerId: instance.widgetRegistration.providerId,
        reason: reason,
        widgetDefinitionId: instance.widgetRegistration.id,
        widgetInstanceId: instance.widgetId,
    });
    notifyWidgetProvider(instance, { errorKind: "cardCreationFailed", message: e.Message ? e.Message : e });
}
